import { ApolloClient } from "@apollo/client";

import { EVENT_ID } from "src/shared/constants/event";
import {
  attendeeReceivesBadge,
  buildBadgeIdentity,
  preferredEventAttendee,
  registrationStageIsEligible,
  registrationWaiverStatus,
} from "src/views/volunteer/registration/badge-registration/badgeData";
import { validateBadgeIdentity } from "src/views/volunteer/registration/badge-registration/badgeProvisioner";
import {
  FindRegistrationAttendeeByEmailDocument,
  FindRegistrationAttendeeByEmailQuery,
  FindRegistrationAttendeeByEmailQueryVariables,
} from "src/views/volunteer/registration/badge-registration/graphql/findRegistrationAttendeeByEmail.generated";
import {
  GetRegistrationBadgeProfileDocument,
  GetRegistrationBadgeProfileQuery,
  GetRegistrationBadgeProfileQueryVariables,
} from "src/views/volunteer/registration/badge-registration/graphql/getRegistrationBadgeProfile.generated";

import {
  GetBadgeBackupDocument,
  GetBadgeBackupQuery,
  GetBadgeBackupQueryVariables,
} from "./graphql/getBadgeBackup.generated";

// Used for the preview and again immediately before setup. A changed account,
// assignment, eligibility or profile must be reviewed before writing the badge.
export async function loadAdminSetupTarget(
  client: ApolloClient<object>,
  email: string
) {
  const { data } = await client.query<
    FindRegistrationAttendeeByEmailQuery,
    FindRegistrationAttendeeByEmailQueryVariables
  >({
    query: FindRegistrationAttendeeByEmailDocument,
    variables: { email: email.trim(), eventId: EVENT_ID },
    fetchPolicy: "no-cache",
  });
  const user = data.users[0];
  const attendee = preferredEventAttendee(
    data.eventAttendees.filter(
      (item) => item.user_id === user?.id && attendeeReceivesBadge(item.role)
    )
  );
  if (!user || !attendee)
    throw new Error("No eligible event attendee was found for that email.");
  const result = await client.query<
    GetRegistrationBadgeProfileQuery,
    GetRegistrationBadgeProfileQueryVariables
  >({
    query: GetRegistrationBadgeProfileDocument,
    variables: { userId: user.id, extendedProfileUserId: user.id },
    fetchPolicy: "no-cache",
  });
  const claim = result.data.claims[0];
  if (
    !claim ||
    claim.id !== data.claims[0]?.id ||
    !registrationStageIsEligible(claim.stage.slug)
  )
    throw new Error(
      "This attendee needs a confirmed RSVP or completed check-in before badge setup."
    );
  const application = claim.fields_HACKTHENORTH2026_APPLICATIONS.answers;
  if (
    !registrationWaiverStatus({
      legalName: application.rsvp_legal_name.value,
      signature: application.rsvp_consent_waiver_signature.value,
      uploadedFiles: application.rsvp_consent_waiver.value,
    }).signed
  )
    throw new Error(
      "This attendee must complete their registration waiver before badge setup."
    );
  const identity = buildBadgeIdentity({
    attendee: {
      eventAttendeeId: attendee.id,
      role: attendee.role,
      registrationData: {
        preferredName:
          data.claims[0].fields_HACKTHENORTH2026_APPLICATIONS.answers
            .rsvp_preferred_name.value,
        legalName:
          application.rsvp_legal_name.value || user.legal_name || user.name,
      },
    },
    profile: {
      claimId: claim.id,
      badgeDisplayName:
        result.data.getExtendedProfile?.scannee
          ?.fields_HACKTHENORTH2026_EXTENDED_PROFILES.answers.badge_display_name
          .value,
      networkingEmail: application.networking_email.value,
      networkingPhone: application.networking_phone_number.value,
      networkingLinkedIn: application.networking_linkedin_link.value,
      networkingDiscord: application.networking_discord_tag.value,
      networkingInstagram: application.networking_instagram_link.value,
      networkingX: application.networking_twitter_link.value,
    },
    accountEmail: user.email,
    badgeId:
      attendee.badge_code && !attendee.badge_code.startsWith("virtual-")
        ? attendee.badge_code
        : "pending-allocation",
    now: 0,
  });
  validateBadgeIdentity(identity);
  const backupResult = await client.query<
    GetBadgeBackupQuery,
    GetBadgeBackupQueryVariables
  >({
    query: GetBadgeBackupDocument,
    variables: { userId: user.id },
    fetchPolicy: "no-cache",
  });
  const backupClaim = backupResult.data.claims.find(
    (item) => item.id === claim.id && item.user_id === user.id
  );
  const backup =
    backupClaim?.fields_HACKTHENORTH2026_APPLICATIONS.answers.badge_data_json;
  return {
    backup: backup?.value
      ? { value: backup.value, updatedAt: backup.updated_at }
      : null,
    userId: user.id,
    claimId: claim.id,
    eventAttendeeId: attendee.id,
    email: user.email,
    assignedBadgeId:
      identity.badge_id === "pending-allocation" ? "" : identity.badge_id,
    identity,
  };
}
